import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, model, OnDestroy, OnInit, output, Signal, signal, ViewChild } from '@angular/core';
import { CommonModule, DatePipe, NgClass, NgStyle, AsyncPipe } from '@angular/common';
import { GridOptions } from '../interfaces/grid-options';
import { GridEmitter } from '../interfaces/grid-emitter';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription, debounceTime, distinctUntilChanged, isObservable } from 'rxjs';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSort, Sort, MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { CustomDatePickerComponent, UI_FORM_IMPORTS } from '@program-suite/ui';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { IconComponent } from '@program-suite/util-icons';
import { ButtonComponent } from '@program-suite/ui';

@Component({
  selector: 'lib-ui-grid',
  templateUrl: './ui-grid.component.html',
  styleUrl: './ui-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatPaginatorModule,
    MatToolbarModule,
    MatSortModule,
    MatTableModule,
    MatFormFieldModule,
    MatOption,
    MatSelectModule,
    ReactiveFormsModule,
    MatCardModule,
    IconComponent,
    ButtonComponent,
    DatePipe,
    NgClass,
    NgStyle,
    AsyncPipe,
    ...UI_FORM_IMPORTS,
    CustomDatePickerComponent
],
})
export class UiGridComponent implements OnInit, AfterViewInit, OnDestroy {
  private fetchAllIntervalId!: number | undefined;
  defaultFetchAllInMinutes = 1;
  gridOptions = model<GridOptions>();
  actionEvent = output<GridEmitter>();

  //#region Utility
  smallScreen!: boolean;
  currentPageNumber = signal(1);
  itemsFrom = computed(() => {
    if (this.totalNumberOfItems() == 0) {
      return 0;
    } else {
      return (((this.currentPageNumber() - 1) * this.itemsPerPage()) + 1);
    }
  });
  itemsTo = computed(() => {
    if (this.itemsFrom() + this.itemsPerPage() - 1 > (this.totalNumberOfItems() || 0)) {
      return this.totalNumberOfItems();
    } else {
      return this.itemsFrom() + this.itemsPerPage() - 1;
    }
  });
  itemsPerPage = signal(20);
  totalNumberOfItems = computed((): number => {
    if (this.afterInit() || this.matDataSource() || this.gridOptions()) {}
    return this.gridOptions()?.totalNumberOfItems ? this.gridOptions()?.totalNumberOfItems?.() ?? 0 : this.gridOptions()?.data().length ?? 0;
  });
  totalNumberOfPages = computed(() => {
    return this.totalNumberOfItems() === 0 ? 1 : Math.ceil(this.totalNumberOfItems() / this.itemsPerPage());
  });
  paginationForm!: FormGroup;
  pageSizeOptions: Array<number> = [20, 40, 60, 100];
  afterInit = signal(false);
  matDataSource = computed(() => {
    var data = this.gridOptions()?.data() ?? [];
    var dataSource = new MatTableDataSource(data);
    if (this.afterInit()) {
      dataSource.sort = this.matSort;
      if (this.gridOptions()?.frontEndPagination) {
        this.paginator.pageIndex = this.currentPageNumber() - 1;
        dataSource.paginator = this.paginator;
      }
    }
    return dataSource;
  });
  mobileData = computed(() => {
    if (this.gridOptions()?.frontEndPagination) {
      return this.matDataSource().data.slice(this.itemsFrom() - 1, this.itemsTo())
    } else {
      return this.matDataSource().data;
    }
  });
  displayedColumns: string[] = [];
  truncateSize!: number;
  //#endregion

  //#region Filter
  visibleFormControlData!: any[];
  searchFormControlData!: any;
  hiddenFormControlData!: any[];
  formValueChangesSub!: Subscription;
  filtersForm: any;
  sortOrder: any;
  isLoading: boolean = false;
  hasFilters: boolean = false;
  openFilters: boolean = true;
  //#endregion

  //#region Grid
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) matSort!: MatSort;
  @ViewChild('truncateCol', { static: false }) truncateCol!: ElementRef;
  //#endregion

  @HostListener('window:resize')
  onResize() {
    this.breakpointObserver.observe(['(min-width: 1024px)']).subscribe((result) => {
      this.smallScreen = !result.matches;
      this.getColumnSize('truncate-col');
    });
  }
  constructor(
    private fb: FormBuilder,
    private breakpointObserver: BreakpointObserver,
  ) {

  }

  ngOnInit(): void {
    this.openFilters = this.gridOptions()?.openFilters ?? false;
    this.buildingFormFilter();
    this.gridOptions()?.columns.forEach((column: any) => {
      if (column.hideColumn !== true) {
        this.displayedColumns.push(column.controlName);
      }
    });

    this.breakpointObserver.observe(['(min-width: 1024px)']).subscribe((result) => {
      this.smallScreen = !result.matches;
    });

    this.paginationForm = this.fb.group({
      pageSize: this.fb.control(this.itemsPerPage()),
    });
    this.paginationForm?.get('pageSize')?.valueChanges.subscribe((val: any) => {
      this.itemsPerPage.set(+val);
      this.currentPageNumber.set(1);
      if (this.paginator && this.gridOptions()?.frontEndPagination) {
        this.paginator._changePageSize(this.itemsPerPage());
      }
      if ((this.gridOptions()?.data()?.length ?? 0 > 0) && !this.gridOptions()?.frontEndPagination && !this.isLoading) {
        this.fetchAll();
      }
    });

    this.formValueChangesSub = this.filtersForm.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPageNumber.set(1);
      this.fetchAll('formChanges');
    });

    this.dependencyFields();
    if ((this.gridOptions()?.data()?.length == 0) && !this.isLoading) {
      this.fetchAll();
    }

    // Start the interval for fetchAll if fetchAllInMinutes is valid
    this.startFetchAllInterval();
  }

  ngAfterViewInit() {
    if (this.sortOrder?.active) {
      this.matSort.active = this.sortOrder.active;
    }
    if (this.sortOrder?.direction) {
      this.matSort.direction = this.sortOrder.direction;
    }
    if ( this.sortOrder?.active || this.sortOrder?.direction) {
      this.matSort.sortChange.next(this.sortOrder);
    }
    this.afterInit.set(true);
  }

  ngOnDestroy(): void {
    this.fetchAll('onDestroy');
    if (this.formValueChangesSub) {
      this.formValueChangesSub.unsubscribe();
    }
    if (this.breakpointObserver) {
      this.breakpointObserver.ngOnDestroy();
    }
    this.clearFetchAllInterval();
  }

  buildingFormFilter() {
    const formControlsConfig: any = {};
    this.visibleFormControlData = [];
    this.hiddenFormControlData = [];
    if (this.gridOptions()?.filterData === undefined || this.gridOptions()?.filterData === null || this.gridOptions()?.filterData.length === 0) {
      this.hasFilters = false;
    } else {
      this.hasFilters = true;
    }
    const filterValues = this.gridOptions()?.filterValues();
    // set default values
    this.sortOrder = filterValues ? (filterValues as { [key: string]: any })['sortOrder'] : undefined;
    this.currentPageNumber.set(filterValues ? (filterValues as { [key: string]: any })['pageNumber'] ?? 1 : 1);
    this.itemsPerPage.set(filterValues ? (filterValues as { [key: string]: any })['pageSize'] ?? 20 : 20);
    this.gridOptions()?.filterData.forEach((filter: any) => {
      if (filter.hideFilter === true) {
        return;
      }
      if (filter.dateOffset === undefined || filter.dateOffset === null) {
        filter.dateOffset = 0;
      }
      let filterValue = filterValues ? (filterValues as { [key: string]: any })[filter.formControlName] : undefined;
      if (filter.type === 'date') {
        // Use original date values if available (these are preserved for form restoration)
        const originalKey = `_original${filter.formControlName.charAt(0).toUpperCase() + filter.formControlName.slice(1)}`;
        const originalValue = filterValues ? (filterValues as { [key: string]: any })[originalKey] : undefined;
        filterValue = originalValue ?? filterValue;
        formControlsConfig[filter.formControlName] = [filterValue ? filterValue : new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + filter.dateOffset)];
        this.visibleFormControlData.push(filter);
      } else if (filter.type === 'search') {
        formControlsConfig[filter.formControlName] = [filterValue ?? filter.defaultValue ?? ''];
        this.searchFormControlData = filter;
      } else if (filter.type === 'searchDropdown') {
        formControlsConfig[filter.formControlName] = [filterValue ?? filter.defaultValue ?? ''];
        this.hiddenFormControlData.push(filter);
      } else if (filter.type === 'checkBox') {
        formControlsConfig[filter.formControlName] = [filterValue ?? filter.defaultValue ?? false];
        this.hiddenFormControlData.push(filter);
      } else {
        formControlsConfig[filter.formControlName] = [filterValue ?? filter.defaultValue ?? ''];
        this.hiddenFormControlData.push(filter);
      }
    });
    this.filtersForm = this.fb.group(formControlsConfig);
  }

  fetchAll(action: string = 'fetchAll') {
    // Check if date filters are configured but not yet set - skip the API call until they're ready
    const hasDateFromFilter = this.gridOptions()?.filterData?.some(f => f.formControlName === 'dateFrom' && f.type === 'date');
    const hasDateToFilter = this.gridOptions()?.filterData?.some(f => f.formControlName === 'dateTo' && f.type === 'date');

    if ((hasDateFromFilter && !this.filtersForm.value.dateFrom) ||
        (hasDateToFilter && !this.filtersForm.value.dateTo)) {
      return; // Don't call API until date pickers are initialized
    }

    this.startFetchAllInterval();
    const params: any = {
      ...this.filtersForm.value,
      pageNumber: this.currentPageNumber(),
      pageSize: this.itemsPerPage(),
      sortOrder: this.sortOrder,
    };
    // Convert dates for API but keep original form values for restoration
    if (this.filtersForm.value.dateFrom) {
      params.dateFrom = this.calculateFromDate(this.filtersForm.value.dateFrom);
      params._originalDateFrom = this.filtersForm.value.dateFrom;
    }
    if (this.filtersForm.value.dateTo) {
      params.dateTo = this.calculateToDate(this.filtersForm.value.dateTo);
      params._originalDateTo = this.filtersForm.value.dateTo;
    }
    this.getColumnSize('truncate-col')
    this.emitAction(action, params);
  }

  calculateFromDate(val: any) {
    if (!val) return val;

    // Extract year, month, day from the value to avoid timezone parsing issues
    const { year, month, day } = this.extractDateParts(val);
    if (this.gridOptions()?.useUtcDates) {
      // Convert local midnight to UTC ISO string for timezone-aware queries
      const localMidnight = new Date(year, month, day, 0, 0, 0, 0);
      return localMidnight.toISOString();
    }
    // Default: return date-only format (YYYY-MM-DD)
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  calculateToDate(val: any) {
    if (!val) return val;

    // Extract year, month, day from the value to avoid timezone parsing issues
    const { year, month, day } = this.extractDateParts(val);
    if (this.gridOptions()?.useUtcDates) {
      // Convert local end-of-day (23:59:59.999) to UTC ISO string for timezone-aware queries
      const localEndOfDay = new Date(year, month, day, 23, 59, 59, 999);
      return localEndOfDay.toISOString();
    }
    // Default: return date-only format (YYYY-MM-DD)
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private extractDateParts(val: any): { year: number; month: number; day: number } {
    // If it's a string in YYYY-MM-DD format, parse it directly to avoid timezone issues
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [year, month, day] = val.split('-').map(Number);
      return { year, month: month - 1, day }; // month is 0-indexed
    }
    // Otherwise, treat as local date
    const date = new Date(val);
    return { year: date.getFullYear(), month: date.getMonth(), day: date.getDate() };
  }

  utcToLocalDate(date: any) {
    // convert UTC date to local date
    if (date) {
      // If the date string ends with 'Z', it's already in UTC format and JavaScript will handle it correctly
      // Otherwise, assume it's a UTC date string without 'Z' and explicitly create a Date object
      const dateStr = typeof date === 'string' ? date : date.toString();
      if (dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('T')) {
        // Already has timezone info, let JavaScript handle it naturally
        return new Date(date);
      } else {
        // No timezone info, assume UTC and convert to local
        return new Date(date + 'Z');
      }
    }
    return date;
  }

  clearFilters() {
    if (this.formValueChangesSub) {
      this.formValueChangesSub.unsubscribe();
    }
    this.currentPageNumber.set(1);

    // Prepare default values for reset
    const resetValues: any = {};
    const filterValues = this.gridOptions()?.filterValues();

    this.gridOptions()?.filterData.forEach((filter: any) => {
      let value;
      if (filter.type === 'date') {
        value = filter.defaultValue
          ? new Date(filter.defaultValue)
          : new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + (filter.dateOffset ?? 0));
      } else if (filter.type === 'checkBox') {
        value = filter.defaultValue ?? false;
      } else {
        value = filter.defaultValue ?? '';
      }
      resetValues[filter.formControlName] = value;
    });

    this.filtersForm.reset(resetValues);

    // Optionally, reset sortOrder if needed
    this.sortOrder = filterValues ? (filterValues as { [key: string]: any })['sortOrder'] : undefined;

    this.formValueChangesSub = this.filtersForm.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPageNumber.set(1);
      this.fetchAll('formChanges');
    });
    this.fetchAll('clearFilters');
  }

  paginationRight() {
    if (this.currentPageNumber() < this.totalNumberOfPages() && !this.isLoading) {
      this.currentPageNumber.update((val) => val + 1);
      if (!this.gridOptions()?.frontEndPagination) {
        this.fetchAll();
      }
    }
  }
  paginationLeft() {
    if (this.currentPageNumber() > 1 && !this.isLoading) {
      this.currentPageNumber.update((val) => val - 1);
      if (!this.gridOptions()?.frontEndPagination) {
        this.fetchAll();
      }
    }
  }


  sortData(sort: Sort) {
    this.sortOrder = sort;
    if (!this.gridOptions()?.frontEndPagination && !this.isLoading && this.afterInit()) {
      this.fetchAll();
    }
  }

  displayOptions(option: any): string {
    return option && option.name ? option.name : option?.length > 0 ? option : '';
  }

  searchButton() {
    this.currentPageNumber.set(1);
    if (!this.gridOptions()?.frontEndPagination && !this.isLoading) {
      this.fetchAll();
    }
  }

  closeFilter() {
    this.openFilters = !this.openFilters;
  }

  actionButton(row: any, buttonAction: string) {
    this.emitAction(buttonAction, row);
  }

  emitAction(ActionName: any, data: any = null) {
    this.actionEvent.emit({
      action: ActionName,
      data: data,
    });
  }

  toArray(obj: any): any[] {
    return Object.keys(obj).map(key => obj[key]);
  }

  isArray(list: any[]) {
    return Array.isArray(list);
  }

  isSignal(value: any) {
    return typeof value === 'function';
  }

  isObservable(value: any) {
    return isObservable(value);
  }

  getColumnSize(Name: string) {
    const columns = document.getElementsByClassName(Name) as HTMLCollectionOf<HTMLElement>;
    if (columns.length > 0) {
      const columnWidth = columns[0].offsetWidth;
      this.truncateSize = columnWidth;
    }
  }

  setGridOptions(options: GridOptions) {
    this.gridOptions.set(options);
  }

  dependencyFields() {
    this.gridOptions()?.filterData.forEach((filter: any) => {
      if (filter.dependencies && filter.dependencies.length > 0) {
        const currentControl = this.filtersForm.get(filter.formControlName);
        
        if (!currentControl) {
          return;
        }
        filter.dependencies.forEach((dependency: string) => {
          const parentControl = this.filtersForm.get(dependency);
  
          if (parentControl && currentControl) {
            // Subscribe to parent control value changes
            parentControl.valueChanges.subscribe(() => {
              currentControl.setValue('', { emitEvent: false });
            });
          }
        });
      }
    });
  }

  private startFetchAllInterval(): void {
    this.clearFetchAllInterval();
    const fetchAllInMinutes = this.gridOptions()?.fetchAllInMinutes ?? this.defaultFetchAllInMinutes;

    if (fetchAllInMinutes && fetchAllInMinutes > 0) {
      this.fetchAllIntervalId = window.setInterval(() => {
        this.fetchAll();
      }, fetchAllInMinutes * 60000); // Convert minutes to milliseconds
    }
  }

  private clearFetchAllInterval(): void {
    if (this.fetchAllIntervalId) {
      window.clearInterval(this.fetchAllIntervalId);
      this.fetchAllIntervalId = undefined;
    }
  }

  camelCaseToTitleCase(value: string): string {
    if (!value) return value;

    // Handle AI prefix
    let prefix = '';
    if (value.startsWith('AI')) {
      prefix = 'AI-';
      value = value.slice(2); // Remove 'AI' from the start
    }

    // Convert camelCase to spaced title case
    const result = value
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, (char) => char.toUpperCase()) // Capitalize first letter
      .trim(); // Remove leading space if any

    return prefix + result;
  }

  dropdownMenuItems(row: any, dropdownItems: any[]) {
    const menuItems: any[] = [];
    for (const item of dropdownItems) {
      menuItems.push({
        name: item.name ?? item.conditionalName?.calculateDisplay(row[item.conditionalName?.column]),
        type: 'button',
        action: () => this.actionButton(row, item.buttonAction),
        disabled: item.conditionalDisabled ? item.conditionalDisabled.calculateDisabled(row[item.conditionalDisabled.column]) : false,
      });
    }
    return menuItems;
  }
}
